import { useRef, useState } from 'react';
import {
  findActiveBranch,
  getCheckedFiles,
  setCheckedChildren,
  updateBranchInTree,
} from 'src/utils/fileUtils';
import type { TreeNode } from 'src/utils/fileUtils';

interface UseKnowledgeBaseSelectionParams {
  tree: TreeNode;
  activeBranch: TreeNode;
  updateActiveBranch: (branch: TreeNode) => void;
}

export const useKnowledgeBaseSelection = ({
  tree,
  activeBranch,
  updateActiveBranch,
}: UseKnowledgeBaseSelectionParams) => {
  const [moveContentMode, setMoveContentMode] = useState(false);
  const [deleteContentMode, setDeleteContentMode] = useState(false);
  const [checkedItemsCount, setCheckedItemsCount] = useState(0);
  const [checkedFilesCount, setCheckedFilesCount] = useState(0);
  // Delete and move handlers reset the selection after awaiting a request, by which time the
  // tree has been rebuilt; resetting from the tree of the click's render would bring back
  // the deleted items.
  const latestTreeRef = useRef(tree);
  latestTreeRef.current = tree;

  const resetCheckedItems = (treeArg?: TreeNode) => {
    const branch = treeArg || latestTreeRef.current;
    setCheckedChildren(branch, false);

    const nextActiveBranch = findActiveBranch(branch);
    setCheckedFilesCount(0);
    setCheckedItemsCount(0);
    if (nextActiveBranch) {
      updateActiveBranch({ ...nextActiveBranch });
    }
  };

  const cancelCheckboxMode = () => {
    setMoveContentMode(false);
    setDeleteContentMode(false);
    resetCheckedItems();
  };

  const handleSelectAllChange = (checked: boolean) => {
    const newBranch = { ...activeBranch };

    setCheckedChildren(newBranch, checked);
    updateBranchInTree(tree, activeBranch, newBranch);
    updateActiveBranch(newBranch);
    onCheckedChange();
  };

  const onCheckedChange = () => {
    if (!moveContentMode && !deleteContentMode) {
      return;
    }

    const checkedItems = getCheckedFiles(tree);

    setCheckedItemsCount(checkedItems.length);
    setCheckedFilesCount(checkedItems.filter((item) => item.type !== 'folder').length);
  };

  return {
    moveContentMode,
    deleteContentMode,
    checkedItemsCount,
    checkedFilesCount,
    onCheckedChange,
    handleSelectAllChange,
    resetCheckedItems,
    cancelCheckboxMode,
    setMoveContentMode,
    setDeleteContentMode,
  };
};
